import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { IApplicationState } from '.';
import Constants from '../constants';
import { ITranslationEditItem } from '../models';
import * as translationsApi from '../services/TranslationsAPI';
import { TranslationHelper } from '../utilities';

export interface ISurveyInstructionsAdminState {
	isLoadingOrSaving: boolean;
	isError: boolean;
	translations: ITranslationEditItem[];
	originalTranslations: { [cultureCode: string]: ITranslationEditItem };
}

export const initialState: ISurveyInstructionsAdminState = {
	isLoadingOrSaving: true,
	isError: false,
	translations: [],
	originalTranslations: {},
};

export const loadSurveyInstructionsAdminData = createAsyncThunk(
	'SurveyInstructionsAdmin/loadSurveyInstructionsAdminData',
	async (surveyId: number, { rejectWithValue }) => {
		try {
			return await translationsApi.getTranslations({
				apiUrl: `${Constants.API_TRANSLATIONS_URL}/${surveyId}/Instructions`,
				uniqueId: surveyId,
			});
		} catch {
			return rejectWithValue([]);
		}
	},
);

export const saveSurveyInstructionsAdminData = createAsyncThunk(
	'SurveyInstructionsAdmin/saveSurveyInstructionsAdminData',
	async (surveyId: number, { getState, rejectWithValue }) => {
		try {
			const {
				surveyInstructionsAdmin: { translations },
				survey: { surveyEditModel },
			} = getState() as IApplicationState;
			return await translationsApi.saveTranslations(
				{ apiUrl: `${Constants.API_TRANSLATIONS_URL}/${surveyId}/Instructions`, uniqueId: surveyId },
				surveyEditModel.defaultCultureCode,
				translations,
			);
		} catch {
			return rejectWithValue([]);
		}
	},
);

const SurveyInstructionsAdminSlice = createSlice({
	name: `SurveyInstructionsAdmin`,
	initialState: initialState,
	reducers: {
		updateSurveyInstructionsTranslation: (state, action: PayloadAction<Pick<ITranslationEditItem, 'cultureCode' | 'textValue'>>) => {
			const {
				payload: { cultureCode, textValue },
			} = action;

			for (const translation of state.translations) {
				if (translation.cultureCode !== cultureCode) {
					continue;
				}

				translation.textValue = textValue;
				translation.editState = TranslationHelper.getEditState(translation, state.originalTranslations[cultureCode]);
				break;
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(isAnyOf(loadSurveyInstructionsAdminData.pending, saveSurveyInstructionsAdminData.pending), (state) => {
				state.isLoadingOrSaving = true;
				state.isError = false;
			})
			.addMatcher(isAnyOf(loadSurveyInstructionsAdminData.fulfilled, saveSurveyInstructionsAdminData.fulfilled), (state, action) => {
				state.isLoadingOrSaving = false;
				state.translations = action.payload;
				state.originalTranslations = state.translations.reduce((map, value) => {
					map[value.cultureCode] = value;
					return map;
				}, {});
			})
			.addMatcher(isAnyOf(loadSurveyInstructionsAdminData.rejected, saveSurveyInstructionsAdminData.rejected), (state) => {
				state.isLoadingOrSaving = false;
				state.isError = true;
			});
	},
});

export const { updateSurveyInstructionsTranslation } = SurveyInstructionsAdminSlice.actions;
export default SurveyInstructionsAdminSlice.reducer;
